import { useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import axios from 'axios';
import { Form, Formik, getIn, setNestedObjectValues } from 'formik';
import { useState } from 'react';

import { SamplekitMessage } from '../../../types/SamplekitMessage';
import { SurchargeMessage } from '../../../types/SurchargeMessage';
import { Button } from '../../cdl/Button/Button';
import { Callout } from '../../cdl/Callout/Callout';
import { Text } from '../../cdl/Text/Text';
import { FormikTextArea } from '../../cdl/TextArea/FormikTextArea';
import { useToasts } from '../../cdl/Toast/useToasts';
import { queries } from '../../common/api/queryKeys/queries';
import { Divider } from '../../common/Divider/Divider';
import { AdvancedError } from '../../common/Error/AdvancedError';
import { FormikDebug } from '../../common/form-elements/formik/FormikDebug';
import { FormikProductTable } from '../../common/FormikProductTable/FormikProductTable';
import { translate } from '../../common/helpers/translate.helper';
import { useNavigateToOriginLocation } from '../../common/hooks/useNavigateToOriginLocation';
import { IconInfoCircle } from '../../common/icons/cdl/InfoCircle';
import { LoadingIndicator } from '../../common/LoadingIndicator/LoadingIndicator';
import { ItemModel } from '../../common/models/ItemModel';
import { createOverlay } from '../../common/Overlay/Overlay';
import { OverlayHeader } from '../../common/Overlay/OverlayHeader';
import { Box } from '../../common/ui/Box';
import { theme } from '../../common/ui/theme';
import AppSettings from '../../constants';
import { useSurvicateContext } from '../../survicate/useSurvicateContext';
import { useOfferDetails } from '../detail/common/hooks/useOfferDetails';
import { useOfferUpdate } from '../detail/common/hooks/useOfferUpdate';
import { useValidateOfferForm } from '../hooks/useValidateOfferForm';
import { OfferModel } from '../model/OfferModel';
import { OfferState } from '../model/OfferState';
import { useMessageCreate } from '../OfferChat/useMessageCreate';

import { FormikGeneralInformation } from './components/FormikGeneralInformation';
import { FormikSpotConfirmTermsModal } from './components/FormikSpotConfirmTermsModal';
import { QuoteAttachments } from './components/QuoteAttachments';
import { useCreateQuoteDocumentTitle } from './hooks/useCreateQuoteDocumentTitle';

export const CreateQuotePage = () => {
    return createOverlay(<CreateQuote />);
};

const CreateQuote = () => {
    const { id } = useParams({ from: '/_app/offer/$id_/quote' });
    const navigateToOriginLocation = useNavigateToOriginLocation();

    const offerQuery = useOfferDetails(id);
    const offerUpdateMutation = useOfferUpdate();
    const queryClient = useQueryClient();
    const [confirmSpotTermsModalOpen, setConfirmSpotTermsModalOpen] = useState(false);
    const { addToast, addErrorToast } = useToasts();
    const messageMutation = useMessageCreate();
    const formikValidate = useValidateOfferForm();

    const survicate = useSurvicateContext();
    const SURVEY_DELAY_IN_MS = 5000;

    useCreateQuoteDocumentTitle(offerQuery.data);

    const handleCancel = () => {
        navigateToOriginLocation();
    };

    if (offerQuery.isPending) {
        return (
            <OverlayHeader onClose={handleCancel}>
                <LoadingIndicator variant="page" />
            </OverlayHeader>
        );
    }

    if (offerQuery.isError || !offerQuery.data) {
        return (
            <OverlayHeader title={translate('offer.quotationProcess.header.createQuote')} onClose={handleCancel}>
                <AdvancedError />
            </OverlayHeader>
        );
    }

    const offer = offerQuery.data;

    const handleSubmit = async (values: OfferModel & { comment?: string }) => {
        const offerRequest = {
            ...values,
            state: OfferState.QUOTED,
            dateDelivery: values.dateDelivery || undefined,
            items: values.items.map((item: ItemModel) => ({
                ...item,
                productId: item.productId || undefined,
                price:
                    item.price?.value !== undefined && item.price?.value?.toString() !== ''
                        ? {
                              value: item.price.value,
                              currency: item.price.currency || 'USD',
                          }
                        : undefined,
            })),
            surcharges: values.surcharges.map((surcharge: SurchargeMessage) => ({
                ...surcharge,
                value:
                    surcharge.value?.value !== undefined && surcharge.value?.value?.toString() !== ''
                        ? { value: Number(surcharge.value.value), currency: 'USD' }
                        : undefined,
            })),
            samplekits: values.samplekits?.map((samplekit: SamplekitMessage) => ({
                ...samplekit,
                value:
                    samplekit.value?.value !== undefined && samplekit.value?.value?.toString() !== ''
                        ? {
                              value: samplekit.value.value,
                              currency: samplekit.value.currency || 'USD',
                          }
                        : undefined,
            })),
        };

        try {
            await offerUpdateMutation.mutateAsync({ offer: offerRequest, offerId: values.id });

            if (values.comment) {
                await messageMutation.mutateAsync({ offerId: offer.id, message: values.comment });
            }

            addToast(translate('offer.quotationProcess.sentSuccessfully'));

            setTimeout(() => {
                survicate?.showSurvey(AppSettings.survicateSurveyId.supplier);
            }, SURVEY_DELAY_IN_MS);

            navigateToOriginLocation();
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 400) {
                addErrorToast(translate('order.validationFailedToast'));
                return;
            }

            addErrorToast(translate('offer.quotationProcess.failedToSend'));
        }
    };

    const handleInvalidateCache = async () => {
        await queryClient.refetchQueries({ queryKey: queries.offers.detail(offer.id).queryKey });
    };

    return (
        <Box paddingBottom={40}>
            <Formik
                initialValues={offer}
                validateOnMount
                validateOnChange={false}
                onSubmit={handleSubmit}
                validate={formikValidate}
            >
                {(formikProps) => {
                    const duplicateItemsError = getIn(formikProps.errors, 'duplicateItemsError');
                    const showDuplicateItemsError = Boolean(
                        duplicateItemsError && (formikProps.submitCount > 0 || formikProps.touched?.items)
                    );

                    return (
                        <Form>
                            <OverlayHeader
                                maxContentWidth={theme.maxContentWidths.wide}
                                title={
                                    offer.isSpot()
                                        ? translate('offer.quotationProcess.header.createOffer')
                                        : translate('offer.quotationProcess.header.createQuote')
                                }
                                onClose={handleCancel}
                                trailingVisual={
                                    <Button
                                        type="button"
                                        onClick={async () => {
                                            if (formikProps.values.isSpot()) {
                                                await formikProps.validateForm();
                                                formikProps.setTouched(setNestedObjectValues(formikProps.errors, true));
                                                formikProps.isValid && setConfirmSpotTermsModalOpen(true);
                                            } else {
                                                await formikProps.submitForm();
                                                formikProps.setTouched(setNestedObjectValues(formikProps.errors, true));
                                            }
                                        }}
                                        emphasis="high"
                                        isLoading={formikProps.values.isSpot() ? false : formikProps.isSubmitting}
                                    >
                                        {formikProps.values.isSpot()
                                            ? translate('offer.quotationProcess.navigationButtons.spot.send')
                                            : translate('offer.quotationProcess.navigationButtons.contracted.send')}
                                    </Button>
                                }
                            >
                                <Box display="grid" padding={32} gap={48}>
                                    <Box>
                                        <Text variant="title">
                                            {translate('offer.quotationProcess.coreData.banner.headline')}
                                        </Text>
                                        <Text variant="body" color="foreground.muted" as="p">
                                            {translate('offer.quotationProcess.coreData.banner.subHeadline')}
                                        </Text>

                                        <Divider marginTop={16} marginBottom={32} />

                                        <FormikGeneralInformation />
                                    </Box>

                                    <Box>
                                        <Text variant="title">
                                            {translate('offer.quotationProcess.products.banner.headline')}
                                        </Text>
                                        <Text variant="body" color="foreground.muted" as="p">
                                            {translate('offer.quotationProcess.products.banner.subHeadline')}
                                        </Text>

                                        <Divider marginTop={16} marginBottom={32} />
                                        {showDuplicateItemsError ? (
                                            <Box marginBottom={16}>
                                                <Callout
                                                    variant="negative"
                                                    description={duplicateItemsError}
                                                    Icon={IconInfoCircle}
                                                />
                                            </Box>
                                        ) : null}

                                        <FormikProductTable
                                            config={{
                                                initialItems: offer.items,
                                                showPrices: true,
                                                editable: true,
                                                editSurcharges: true,
                                                editPrices: true,
                                            }}
                                        />
                                    </Box>
                                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={16} alignContent="top">
                                        <FormikTextArea
                                            name="comment"
                                            rows={5}
                                            label={translate('offer.quotationProcess.messageToCustomer')}
                                        />
                                        <Box marginTop={24}>
                                            <QuoteAttachments offer={offer} invalidateCache={handleInvalidateCache} />
                                        </Box>
                                    </Box>
                                </Box>

                                <FormikSpotConfirmTermsModal
                                    open={confirmSpotTermsModalOpen}
                                    onDismiss={() => setConfirmSpotTermsModalOpen(false)}
                                />
                                <FormikDebug />
                            </OverlayHeader>
                        </Form>
                    );
                }}
            </Formik>
        </Box>
    );
};
